import { Suspense, lazy } from "react";
import { Link, NavLink, Navigate, Route, Routes } from "react-router";
import { SignInButton } from "./components/SignInButton";
import { useActiveModel } from "./lib/activeModel";
import { useAuth } from "./lib/auth";
import { WelcomePage } from "./pages/Welcome";
import { NotFoundPage } from "./pages/NotFound";
import { TermsPage } from "./pages/Terms";
import { PrivacyPage } from "./pages/Privacy";
import { ResponsibleGamingPage } from "./pages/ResponsibleGaming";
import { LoginPage } from "./pages/Login";
import { RedeemPage } from "./pages/Redeem";
import { VerifyEmailPage } from "./pages/VerifyEmail";
import { ResetPasswordPage } from "./pages/ResetPassword";

// Heavy authenticated views are code-split so logged-out visitors landing on
// the welcome page never download the app/data-table bundle (Principle IV, R8).
const SlatePage = lazy(() =>
  import("./pages/Slate").then((m) => ({ default: m.SlatePage })),
);
const ModelLabPage = lazy(() =>
  import("./pages/ModelLab").then((m) => ({ default: m.ModelLabPage })),
);
const AdminPage = lazy(() =>
  import("./pages/admin/Admin").then((m) => ({ default: m.AdminPage })),
);

function AppHeader() {
  const { user, role } = useAuth();
  const { summary, error } = useActiveModel();

  return (
    <header className="app-header">
      <Link to="/" className="brand">
        <span className="brand-mark">PB</span>
        <span className="brand-name">Predabet</span>
      </Link>
      <nav className="app-nav">
        <NavLink to="/" end>
          Predictions
        </NavLink>
        <NavLink to="/lab">Model Lab</NavLink>
        {role === "admin" ? <NavLink to="/admin">Admin</NavLink> : null}
      </nav>
      {user && summary ? (
        <div className="app-active-model-pill" title={`Run ${summary.runId}`}>
          <span className="app-active-model-dot" aria-hidden />
          <span className="app-active-model-text">
            Active · <strong>{summary.configLabel}</strong> ·{" "}
            <code>{summary.runShort}…</code>
          </span>
        </div>
      ) : user && !summary && error ? (
        <span
          className="app-active-model-pill app-active-model-pill--warn"
          title={error}
        >
          Model status unavailable
        </span>
      ) : null}
      <div className="app-auth">
        <SignInButton />
      </div>
    </header>
  );
}

export function App() {
  const { user } = useAuth();

  // Logged-out shell: a chrome-free splash at `/` plus the public/utility routes
  // that must work signed out. Everything else bounces to the welcome page.
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<WelcomePage />} />
        <Route path="/redeem" element={<RedeemPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/responsible-gaming" element={<ResponsibleGamingPage />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <div className="app-shell">
      <AppHeader />

      <main className="app-main">
        <Suspense fallback={<div className="app-route-loading" aria-busy />}>
          <Routes>
            <Route path="/" element={<SlatePage />} />
            <Route path="/history" element={<Navigate to="/" replace />} />
            <Route path="/lab" element={<ModelLabPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/redeem" element={<RedeemPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route
              path="/responsible-gaming"
              element={<ResponsibleGamingPage />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>

      <footer className="app-footer">
        <span className="app-footer-left">
          Predabet &middot; WNBA probabilistic predictions &middot; For research
          and entertainment only. No betting advice.
        </span>
        <span className="app-footer-links">
          <Link to="/responsible-gaming">Responsible gaming</Link>
          <span aria-hidden>·</span>
          <Link to="/privacy">Privacy</Link>
          <span aria-hidden>·</span>
          <Link to="/terms">Terms</Link>
        </span>
      </footer>
    </div>
  );
}
